import {
  AccountingMilestone,
  AccountingModule,
  AccountingRequirement,
  AccountingSnapshotSummary,
  CapitalLedgerEntry,
} from '../types';

const DAYS_IN_YEAR = 365;

type CapitalRow = {
  sourceEntryId: string;
  module: AccountingModule;
  requirementId?: string;
  milestoneId?: string;
  note: string;
  principal: number;
  annualInterestRate: number;
  startDate: string;
  endDate: string;
  daysInInvest: number;
  accruedInterest: number;
  kind: 'active-principal' | 'withdrawn-principal';
};

type OpenPrincipalLot = {
  sourceEntryId: string;
  module: AccountingModule;
  requirementId?: string;
  milestoneId?: string;
  note: string;
  annualInterestRate: number;
  startDate: Date;
  endDate?: Date;
  remainingPrincipal: number;
};

export interface UnifiedAccountingSnapshot {
  summary: AccountingSnapshotSummary;
  rows: CapitalRow[];
}

export interface UnifiedAccountingReportTemplate {
  generatedAt: string;
  asOfDate: string;
  summary: AccountingSnapshotSummary;
  formulas: {
    netInvestorProfit: 'P = R_e - C - T';
    accruedInterest: 'sum((principal * annualInterestRate) * (daysInInvest / 365))';
    totalValue: 'totalPrincipal + totalAccruedInterest + netInvestorProfit';
  };
  sections: {
    milestoneFinancials: Array<{
      milestoneId: string;
      code: string;
      title: string;
      module: AccountingModule;
      budget: number;
      investment: number;
      actualCost: number;
      taxes: number;
      finalRevenue: number;
    }>;
    ledgerRows: CapitalRow[];
  };
}

export interface UnifiedAccountingValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

const toDate = (value?: string): Date | null => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const toDateString = (value: Date): string => value.toISOString().split('T')[0];

const clampEndDate = (entryEndDate: Date | null, asOfDate: Date): Date => {
  if (!entryEndDate) return asOfDate;
  return entryEndDate.getTime() < asOfDate.getTime() ? entryEndDate : asOfDate;
};

const daysBetween = (startDate: Date, endDate: Date): number => {
  const diffMs = endDate.getTime() - startDate.getTime();
  if (diffMs <= 0) return 0;
  return Math.floor(diffMs / (1000 * 60 * 60 * 24));
};

export const calculateDaysInInvest = (startDate: string, endDate: string): number => {
  const start = toDate(startDate);
  const end = toDate(endDate);
  if (!start || !end) return 0;
  return daysBetween(start, end);
};

export const calculateAccruedInterest = (
  amount: number,
  annualInterestRate: number,
  daysInInvest: number
): number => {
  if (!amount || !annualInterestRate || !daysInInvest) return 0;
  return amount * annualInterestRate * (daysInInvest / DAYS_IN_YEAR);
};

export const computeUnifiedAccountingSnapshot = (
  entries: CapitalLedgerEntry[],
  milestones: AccountingMilestone[],
  asOfDate?: string,
  finalRevenueOverride?: number
): UnifiedAccountingSnapshot => {
  const snapshotDate = toDate(asOfDate) || new Date();
  const sortedEntries = [...entries].sort((a, b) => {
    const left = toDate(a.startDate)?.getTime() || 0;
    const right = toDate(b.startDate)?.getTime() || 0;
    return left - right;
  });

  const openLots: OpenPrincipalLot[] = [];
  const rows: CapitalRow[] = [];

  for (const entry of sortedEntries) {
    const start = toDate(entry.startDate);
    if (!start || start.getTime() > snapshotDate.getTime()) {
      continue;
    }

    if (entry.amount >= 0) {
      openLots.push({
        sourceEntryId: entry.id,
        module: entry.module,
        requirementId: entry.requirementId,
        milestoneId: entry.milestoneId,
        note: entry.note,
        annualInterestRate: entry.annualInterestRate,
        startDate: start,
        endDate: toDate(entry.endDate) || undefined,
        remainingPrincipal: entry.amount,
      });
      continue;
    }

    let remainingWithdrawal = Math.abs(entry.amount);
    for (const lot of openLots) {
      if (remainingWithdrawal <= 0) break;
      if (lot.remainingPrincipal <= 0) continue;

      const applied = Math.min(lot.remainingPrincipal, remainingWithdrawal);
      const lotEndByRule = clampEndDate(lot.endDate || null, snapshotDate);
      const effectiveWithdrawalDate = start.getTime() < lotEndByRule.getTime() ? start : lotEndByRule;
      const days = daysBetween(lot.startDate, effectiveWithdrawalDate);
      const interest = calculateAccruedInterest(applied, lot.annualInterestRate, days);

      rows.push({
        sourceEntryId: lot.sourceEntryId,
        module: lot.module,
        requirementId: lot.requirementId,
        milestoneId: lot.milestoneId,
        note: `${lot.note} (withdrawn on ${toDateString(start)})`,
        principal: applied,
        annualInterestRate: lot.annualInterestRate,
        startDate: toDateString(lot.startDate),
        endDate: toDateString(effectiveWithdrawalDate),
        daysInInvest: days,
        accruedInterest: interest,
        kind: 'withdrawn-principal',
      });

      lot.remainingPrincipal -= applied;
      remainingWithdrawal -= applied;
    }
  }

  for (const lot of openLots) {
    if (lot.remainingPrincipal <= 0) continue;

    const effectiveEnd = clampEndDate(lot.endDate || null, snapshotDate);
    const days = daysBetween(lot.startDate, effectiveEnd);
    const interest = calculateAccruedInterest(lot.remainingPrincipal, lot.annualInterestRate, days);

    rows.push({
      sourceEntryId: lot.sourceEntryId,
      module: lot.module,
      requirementId: lot.requirementId,
      milestoneId: lot.milestoneId,
      note: lot.note,
      principal: lot.remainingPrincipal,
      annualInterestRate: lot.annualInterestRate,
      startDate: toDateString(lot.startDate),
      endDate: toDateString(effectiveEnd),
      daysInInvest: days,
      accruedInterest: interest,
      kind: 'active-principal',
    });
  }

  const totalPrincipal = rows
    .filter((row) => row.kind === 'active-principal')
    .reduce((sum, row) => sum + row.principal, 0);
  const totalAccruedInterest = rows.reduce((sum, row) => sum + row.accruedInterest, 0);

  const totalBudget = milestones.reduce((sum, milestone) => sum + (milestone.budget || 0), 0);
  const totalInvestment = milestones.reduce((sum, milestone) => sum + (milestone.investment || 0), 0);
  const totalActualCost = milestones.reduce((sum, milestone) => sum + (milestone.actualCost || 0), 0);
  const totalTaxes = milestones.reduce((sum, milestone) => sum + (milestone.taxes || 0), 0);
  const finalRevenue =
    typeof finalRevenueOverride === 'number'
      ? finalRevenueOverride
      : milestones.reduce((sum, milestone) => sum + (milestone.finalRevenue || 0), 0);

  const netInvestorProfit = finalRevenue - totalActualCost - totalTaxes;
  const totalValue = totalPrincipal + totalAccruedInterest + netInvestorProfit;

  return {
    summary: {
      asOfDate: toDateString(snapshotDate),
      totalPrincipal,
      totalAccruedInterest,
      totalBudget,
      totalInvestment,
      totalActualCost,
      totalTaxes,
      finalRevenue,
      netInvestorProfit,
      totalValue,
    },
    rows,
  };
};

export const createUnifiedAccountingReportTemplate = (
  entries: CapitalLedgerEntry[],
  milestones: AccountingMilestone[],
  asOfDate?: string,
  finalRevenueOverride?: number
): UnifiedAccountingReportTemplate => {
  const snapshot = computeUnifiedAccountingSnapshot(entries, milestones, asOfDate, finalRevenueOverride);

  return {
    generatedAt: new Date().toISOString(),
    asOfDate: snapshot.summary.asOfDate,
    summary: snapshot.summary,
    formulas: {
      netInvestorProfit: 'P = R_e - C - T',
      accruedInterest: 'sum((principal * annualInterestRate) * (daysInInvest / 365))',
      totalValue: 'totalPrincipal + totalAccruedInterest + netInvestorProfit',
    },
    sections: {
      milestoneFinancials: milestones.map((milestone) => ({
        milestoneId: milestone.id,
        code: milestone.code,
        title: milestone.title,
        module: milestone.module,
        budget: milestone.budget || 0,
        investment: milestone.investment || 0,
        actualCost: milestone.actualCost || 0,
        taxes: milestone.taxes || 0,
        finalRevenue: milestone.finalRevenue || 0,
      })),
      ledgerRows: snapshot.rows,
    },
  };
};

export const validateUnifiedAccountingInputs = (
  requirements: AccountingRequirement[],
  milestones: AccountingMilestone[],
  entries: CapitalLedgerEntry[],
  enabledModules: { sr?: boolean; fcm?: boolean } | undefined
): UnifiedAccountingValidationResult => {
  const errors: string[] = [];
  const warnings: string[] = [];

  const enabled = new Set<AccountingModule>();
  if (enabledModules?.sr) enabled.add('SR');
  if (enabledModules?.fcm) enabled.add('FCM');

  const requirementIdSet = new Set(requirements.map((req) => req.id));
  const milestoneIdSet = new Set(milestones.map((ms) => ms.id));

  const requirementCodeSet = new Set<string>();
  requirements.forEach((req, idx) => {
    const prefix = `Requirement #${idx + 1}`;
    if (!req.code?.trim()) errors.push(`${prefix} is missing code.`);
    if (!req.title?.trim()) errors.push(`${prefix} is missing title.`);
    if (!enabled.has(req.module)) {
      errors.push(`${prefix} uses module ${req.module} which is not enabled.`);
    }
    const codeKey = (req.code || '').trim().toLowerCase();
    if (codeKey) {
      if (requirementCodeSet.has(codeKey)) {
        errors.push(`${prefix} has duplicate code '${req.code}'.`);
      }
      requirementCodeSet.add(codeKey);
    }
    req.milestoneIds.forEach((milestoneId) => {
      if (!milestoneIdSet.has(milestoneId)) {
        errors.push(`${prefix} references unknown milestone '${milestoneId}'.`);
      }
    });
  });

  const milestoneCodeSet = new Set<string>();
  milestones.forEach((ms, idx) => {
    const prefix = `Milestone #${idx + 1}`;
    if (!ms.code?.trim()) errors.push(`${prefix} is missing code.`);
    if (!ms.title?.trim()) errors.push(`${prefix} is missing title.`);
    if (!enabled.has(ms.module)) {
      errors.push(`${prefix} uses module ${ms.module} which is not enabled.`);
    }
    const codeKey = (ms.code || '').trim().toLowerCase();
    if (codeKey) {
      if (milestoneCodeSet.has(codeKey)) {
        errors.push(`${prefix} has duplicate code '${ms.code}'.`);
      }
      milestoneCodeSet.add(codeKey);
    }
    ms.requirementIds.forEach((requirementId) => {
      if (!requirementIdSet.has(requirementId)) {
        errors.push(`${prefix} references unknown requirement '${requirementId}'.`);
      }
    });
  });

  entries.forEach((entry, idx) => {
    const prefix = `Ledger row #${idx + 1}`;
    if (!enabled.has(entry.module)) {
      errors.push(`${prefix} uses module ${entry.module} which is not enabled.`);
    }
    if (!entry.note?.trim()) {
      errors.push(`${prefix} note is required.`);
    }
    if (!entry.startDate) {
      errors.push(`${prefix} start date is required.`);
    }
    if (entry.amount === 0) {
      errors.push(`${prefix} amount cannot be 0.`);
    }
    if (entry.annualInterestRate < 0 || entry.annualInterestRate > 1) {
      errors.push(`${prefix} annual interest rate must be between 0 and 1.`);
    }
    if (!entry.requirementId && !entry.milestoneId) {
      errors.push(`${prefix} must be tagged to at least one requirement or milestone.`);
    }
    if (entry.requirementId && !requirementIdSet.has(entry.requirementId)) {
      errors.push(`${prefix} references unknown requirement '${entry.requirementId}'.`);
    }
    if (entry.milestoneId && !milestoneIdSet.has(entry.milestoneId)) {
      errors.push(`${prefix} references unknown milestone '${entry.milestoneId}'.`);
    }
    const start = toDate(entry.startDate);
    const end = toDate(entry.endDate);
    if (!start) {
      errors.push(`${prefix} start date is invalid.`);
    }
    if (entry.endDate && !end) {
      errors.push(`${prefix} end date is invalid.`);
    }
    if (start && end && end.getTime() < start.getTime()) {
      errors.push(`${prefix} end date cannot be before start date.`);
    }
    if (entry.amount < 0 && !entry.endDate) {
      warnings.push(`${prefix} is a withdrawal without end date; treated as same-day adjustment.`);
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
};

const escapeCsv = (value: string | number): string => {
  const raw = String(value ?? '');
  if (/[",\n]/.test(raw)) {
    return `"${raw.replace(/"/g, '""')}"`;
  }
  return raw;
};

export const buildUnifiedAccountingCsv = (
  report: UnifiedAccountingReportTemplate
): string => {
  const lines: string[] = [];

  lines.push('Section,Metric,Value');
  lines.push(`Summary,As Of,${escapeCsv(report.asOfDate)}`);
  lines.push(`Summary,Total Principal,${escapeCsv(report.summary.totalPrincipal)}`);
  lines.push(`Summary,Total Accrued Interest,${escapeCsv(report.summary.totalAccruedInterest)}`);
  lines.push(`Summary,Total Budget,${escapeCsv(report.summary.totalBudget)}`);
  lines.push(`Summary,Total Investment,${escapeCsv(report.summary.totalInvestment)}`);
  lines.push(`Summary,Total Actual Cost,${escapeCsv(report.summary.totalActualCost)}`);
  lines.push(`Summary,Total Taxes,${escapeCsv(report.summary.totalTaxes)}`);
  lines.push(`Summary,Final Revenue,${escapeCsv(report.summary.finalRevenue)}`);
  lines.push(`Summary,Net Investor Profit,${escapeCsv(report.summary.netInvestorProfit)}`);
  lines.push(`Summary,Total Value,${escapeCsv(report.summary.totalValue)}`);
  lines.push('');

  lines.push('MilestoneId,Code,Title,Module,Budget,Investment,ActualCost,Taxes,FinalRevenue');
  report.sections.milestoneFinancials.forEach((row) => {
    lines.push([
      escapeCsv(row.milestoneId),
      escapeCsv(row.code),
      escapeCsv(row.title),
      escapeCsv(row.module),
      escapeCsv(row.budget),
      escapeCsv(row.investment),
      escapeCsv(row.actualCost),
      escapeCsv(row.taxes),
      escapeCsv(row.finalRevenue),
    ].join(','));
  });
  lines.push('');

  lines.push('EntryId,Module,RequirementId,MilestoneId,Note,Principal,AnnualRate,StartDate,EndDate,DaysInInvest,AccruedInterest,Kind');
  report.sections.ledgerRows.forEach((row) => {
    lines.push([
      escapeCsv(row.sourceEntryId),
      escapeCsv(row.module),
      escapeCsv(row.requirementId || ''),
      escapeCsv(row.milestoneId || ''),
      escapeCsv(row.note),
      escapeCsv(row.principal),
      escapeCsv(row.annualInterestRate),
      escapeCsv(row.startDate),
      escapeCsv(row.endDate),
      escapeCsv(row.daysInInvest),
      escapeCsv(row.accruedInterest),
      escapeCsv(row.kind),
    ].join(','));
  });

  return lines.join('\n');
};

export const isAccountingModuleEnabled = (
  modules: { sr?: boolean; fcm?: boolean } | undefined,
  module: AccountingModule
): boolean => {
  if (!modules) return false;
  return module === 'SR' ? !!modules.sr : !!modules.fcm;
};

export const REFERENCE_LEDGER_TRANSACTIONS: Array<
  Pick<CapitalLedgerEntry, 'startDate' | 'endDate' | 'amount' | 'note' | 'annualInterestRate'>
> = [
  {
    startDate: '2025-08-04',
    endDate: '2026-03-21',
    amount: 25000,
    note: 'Half of Earnest Money',
    annualInterestRate: 0.1,
  },
  {
    startDate: '2025-08-06',
    endDate: '2026-03-21',
    amount: 200,
    note: 'LLC formation',
    annualInterestRate: 0.1,
  },
  {
    startDate: '2025-08-14',
    endDate: '2026-03-21',
    amount: 355000,
    note: 'Wire from Fidelity',
    annualInterestRate: 0.1,
  },
  {
    startDate: '2025-09-22',
    amount: -20000,
    note: 'Withdrawal - Fidelity',
    annualInterestRate: 0.1,
  },
  {
    startDate: '2025-09-12',
    endDate: '2026-03-21',
    amount: 430.44,
    note: 'Paid for demolition permit',
    annualInterestRate: 0.1,
  },
];
